import React, {
  useState,
  useMemo,
  useEffect,
  useRef,
  useCallback,
} from "react";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from "../components/ui/card";
import TiptapEditor from "../components/ui/TiptapEditor";
import { Button } from "../components/ui/button";
import { useResourceProfileByIdQuery } from "../api/sandboxManagementApi";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import { RadioGroup, RadioGroupItem } from "../components/ui/radio-group";
import { Badge } from "../components/ui/badge";
import { toast } from "sonner";
import {
  ArrowLeft,
  FileText,
  Home,
  FolderOpen,
  Copy,
  Target,
  BookOpen,
  CheckCircle,
  Award,
  X,
  ArrowRight,
} from "lucide-react";
import {
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbLink,
  BreadcrumbList,
  BreadcrumbPage,
  BreadcrumbSeparator,
} from "../components/ui/breadcrumb";

import {
  checkpoints,
  CreateCheckPointResponse,
  CreateProjectResponse,
  Difficulty,
  ProjectData,
  UpdateProjectResponse,
  useCoursesQuery,
  useCreateCheckPointMutation,
  useCreateProjectMutation,
  useCreateRubricMutation,
  useDatasetMutation,
  useEditProjectMutation,
  useUpdateCheckPointsMutation,
  useStaffMemberQuery,
  useUpdateRubricMutation,
  useRemoveDatasetMutation,
  useSaveAsTemplateMutation,
  useCreateTemplateWithProjectIdMutation,
  useUpdateTemplateMutation,
  useCreateProjectFromTemplateMutation,
  useUpdateProjectMutation,
  useResourceProfilesDropdownQuery,
} from "../api/createProjectApi";
import {
  useLinkDatasetMutation,
  useCreateDatasetMutation as useCreateDatasetRepoMutation,
  useUpdateDatasetMutation,
} from "../api/createDatasetApi";
import paths from "../routes";
import {
  useNavigate,
  useParams,
  useSearchParams,
  useLocation,
} from "react-router-dom";
import { useQuery, useQueryClient } from "@tanstack/react-query";

// Import extracted components
import { StepIndicator } from "../components/CreateProject";
import { ProjectDetailsStep } from "./CreateProject/ProjectDetailsStep";
import { ProjectAssetsStep } from "./CreateProject/ProjectAssetsStep";
import { ReviewCheckpointsStep } from "./CreateProject/ReviewCheckpointsStep";
import { ProjectSettingsStep } from "./CreateProject/ProjectSettingsStep";

import {
  getProjectById,
  getTemplateById,
  useGenerateEvaluationScript,
} from "../api/projectManagementApi";
import {
  TemplateData,
  CreateProjectPageProps,
  ProjectDetailsStepProps,
} from "./CreateProject/types";
import { EnhancedProject } from "../api/projectManagementApi";
import ConfirmDialog from "../components/ui/ConfirmDialog";

// Tag Input Component
const TagInput = React.memo(
  ({
    tags,
    setTags,
    placeholder,
  }: {
    tags: string[];
    setTags: (tags: string[]) => void;
    placeholder?: string;
  }) => {
    const [inputValue, setInputValue] = useState("");

    // Safety check: ensure tags is always an array
    const safeTags = tags || [];

    const handleKeyDown = (e: React.KeyboardEvent) => {
      if (e.key === "Enter" || e.key === ",") {
        e.preventDefault();
        addTag();
      } else if (
        e.key === "Backspace" &&
        inputValue === "" &&
        safeTags.length > 0
      ) {
        removeTag(safeTags.length - 1);
      }
    };

    const addTag = () => {
      const trimmedValue = inputValue.trim();
      if (trimmedValue && !safeTags.includes(trimmedValue)) {
        setTags([...safeTags, trimmedValue]);
        setInputValue("");
      }
    };

    const removeTag = (index: number) => {
      setTags(safeTags.filter((_, i) => i !== index));
    };

    return (
      <div className="space-y-2">
        <div className="flex flex-wrap gap-2 p-3 border rounded-lg min-h-[42px] focus-within:ring-2 focus-within:ring-blue-500 focus-within:border-blue-500">
          {safeTags.map((tag, index) => (
            <Badge
              key={`tag-${tag}-${index}`}
              variant="secondary"
              className="flex items-center gap-1 pr-1"
            >
              <span>{tag}</span>
              <Button
                type="button"
                variant="ghost"
                size="sm"
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  removeTag(index);
                }}
                className="ml-1 hover:bg-red-100 rounded-full p-0.5 h-auto w-auto"
              >
                <X className="h-3 w-3 hover:text-red-500" />
              </Button>
            </Badge>
          ))}
          <input
            type="text"
            value={inputValue}
            onChange={(e) => setInputValue(e.target.value)}
            onKeyDown={handleKeyDown}
            onBlur={addTag}
            placeholder={safeTags.length === 0 ? placeholder : ""}
            className="flex-1 outline-none bg-transparent min-w-[120px]"
          />
        </div>
        <p className="text-xs text-gray-500">
          Press Enter or comma to add tags. Click the X to remove tags.
        </p>
      </div>
    );
  },
);

// Helper function for dataset linking logic
// Note: Backend automatically handles unlinking when linking a new dataset
async function handleDatasetLinking(
  currentDatasetIds: string[] | undefined,
  originalDatasetIds: string[] | undefined,
  projectId: string,
  linkMutation: any,
  updateOriginalIds: (ids: string[]) => void,
): Promise<{ success: boolean; message?: string }> {
  const current = currentDatasetIds || [];
  const original = originalDatasetIds || [];

  // Check if datasets have changed
  const hasChanged =
    current.length !== original.length ||
    !current.every((id) => original.includes(id));

  // No changes made
  if (!hasChanged) {
    if (current.length > 0) {
      toast.info("Datasets unchanged");
    }
    return { success: true };
  }

  // Link datasets (backend handles unlinking automatically)
  if (current.length > 0) {
    try {
      const linkResponse = await linkMutation.mutateAsync({
        projectId,
        datasetIds: current,
      });

      if (!linkResponse.isSuccess) {
        return { success: false, message: "Failed to link datasets" };
      }

      toast.success(`${current.length} dataset(s) linked successfully`);
      updateOriginalIds(current);
      return { success: true };
    } catch (error) {
      console.error("[Dataset Link Error]", error);
      return {
        success: false,
        message: "Failed to link datasets",
      };
    }
  }

  // All datasets were removed
  // Backend will handle cleanup
  toast.info("All datasets removed");
  updateOriginalIds([]);
  return { success: true };
}

// Helper function to create datasets from uploaded files and link them to project
async function handleUploadedFilesToDatasets(
  uploadedFiles: any[],
  projectId: string,
  projectTitle: string,
  createDatasetMutation: any,
  linkMutation: any,
): Promise<{
  success: boolean;
  createdDatasetIds: string[];
  message?: string;
}> {
  if (!uploadedFiles || uploadedFiles.length === 0) {
    return { success: true, createdDatasetIds: [] };
  }

  const createdDatasetIds: string[] = [];

  try {
    // Create datasets for each uploaded file
    for (const fileData of uploadedFiles) {
      if (!fileData.file) continue; // Skip if no actual file

      const formData = new FormData();
      formData.append("title", `${projectTitle}-Dataset`);
      formData.append("projectId", projectId);
      formData.append("datasetFile", fileData.file);
      formData.append("visibility", "PROJECT");

      const response = await createDatasetMutation.mutateAsync(formData);

      if (response.isSuccess && response.data?.id) {
        createdDatasetIds.push(response.data.id);
      }
    }

    // Link all created datasets to the project
    if (createdDatasetIds.length > 0) {
      const linkResponse = await linkMutation.mutateAsync({
        projectId,
        datasetIds: createdDatasetIds,
      });

      if (!linkResponse.isSuccess) {
        return {
          success: false,
          createdDatasetIds: [],
          message: "Failed to link uploaded datasets",
        };
      }

      toast.success(`${createdDatasetIds.length} file(s) uploaded as datasets`);
    }

    return { success: true, createdDatasetIds };
  } catch (error) {
    console.error("[Upload Files to Datasets Error]", error);
    return {
      success: false,
      createdDatasetIds: [],
      message: "Failed to create datasets from uploaded files",
    };
  }
}

export default function CreateProjectPage({
  templateData,
}: CreateProjectPageProps) {
  const [searchParams] = useSearchParams();
  const [templateCategory, setTemplateCategory] = useState("");
  const [templateSubcategory, setTemplateSubcategory] = useState("");
  //const [savingTemplate, setSavingTemplate] = useState(false);
  const { projectId } = useParams();
  const mode = searchParams.get("mode") || (projectId ? "edit" : "create");
  const action = searchParams.get("action");
  const [dataToMatch, setDataToMatch] = useState<EnhancedProject | null>(null);
  const [buttonLoader, setButtonLoader] = useState(false);
  const [temporaryProjectId, setTemporaryProjectId] = useState("");
  const [tempCheckPointData, setTempCheckPointData] = useState<any>({});
  const [aiScriptPopupState, setAiScriptPopupState] = useState<{
    isOpen: boolean;
    projectId: string | null;
  }>({ isOpen: false, projectId: null });
  const [saveTemplatePopupState, setSaveTemplatePopupState] = useState<{
    isOpen: boolean;
    projectId: string | null;
  }>({ isOpen: false, projectId: null });
  // const { data, isLoading, error } = useQuery({
  //   queryKey: ["project", projectId],
  //   queryFn: () => getProjectById(projectId || ""),
  //   enabled: !!projectId && mode === "edit",  // condition moved here
  // });

  const [baseProjectData, setBaseProjectData] = useState<ProjectData | null>(
    null,
  );

  // Read step from URL parameter, default to 1 if not present
  const stepParam = searchParams.get("step");
  const initialStep = stepParam ? parseInt(stepParam, 10) : 1;
  const [currentStep, setCurrentStep] = useState(initialStep);

  const [selectedCheckpointId, setSelectedCheckpointId] = useState(1);
  const [savingTemplate, setSavingTemplate] = useState(false);
  const [localNavigate, setLocalNavigate] = useState(false);

  useEffect(() => {
    setBaseProjectData(projectData);
  }, []);

  const createProjectMutation = useCreateProjectMutation();
  const uploadDataSetMutation = useDatasetMutation();
  const location = useLocation();
  const locationState = location.state;
  const generateScriptMutation = useGenerateEvaluationScript();
  const linkDatasetMutation = useLinkDatasetMutation();
  const createDatasetRepoMutation = useCreateDatasetRepoMutation();
  const updateDatasetMutation = useUpdateDatasetMutation();
  const createProjectWithTemplateMutation =
    useCreateProjectFromTemplateMutation();
  // const getTemplateByIdMutation = useGetTemplateByIdQuery()
  const updateCheckPointsMutation = useUpdateCheckPointsMutation();
  const createCheckpointMutation = useCreateCheckPointMutation();
  const createRubricMutation = useCreateRubricMutation();
  const updateMutation = useUpdateTemplateMutation();
  const updateTemplateDataMutation = useUpdateProjectMutation();
  const [dataSetsForTemplate, setDataSetsForTemplate] = useState<File[] | null>(
    null,
  );
  const updateRubricMutation = useUpdateRubricMutation();
  // fetch courses with React Query (inside component body — valid)
  const shouldFetchCourses =
    mode === "edit" || (mode === "template" && action === "edit");

  const {
    data: courses,
    isLoading: coursesLoading,
    isError: coursesError,
    error: coursesErrorObj,
  } = useCoursesQuery(shouldFetchCourses);

  const getDefaultProjectData = (): ProjectData => ({
    // Step 1: Project Details
    id: "",
    title: "",
    // categoryId: "category abc",
    courseId: "",
    description: "",
    instructorIds: [],
    teachingAssId: [],
    saveAsTemplate: false,
    templateCategory: "",
    templateSubCategory: "",
    projectType: "individual",
    difficulty_level: "beginner",
    totalPoints: 100,
    estimatedHours: "",
    tags: [],
    project_overview: "",
    learning_objectives: "",
    prerequisites: "",
    isScreen: 1,
    sandbox_time_duration: "",
    dueDate: "",
    resource_profile_id: "",

    projectId: "",
    instructions: "",
    datasets: [],

    checkpoints: [
      {
        project_id: "",
        isScreen: 3,
        title: "Checkpoint 1",
        description: "",
        checkpoint_number: 1,

        start_date: "",
        due_date: "",
        weight_percentage: 0,
        is_required: false,
        status: "draft",
        metadata: {},
      },
    ],

    // Step 4: Project Settings
    sandboxResourceProfile: "",
    maxSubmissions: 0,
    lateSubmissionsAllowed: false,

    acceptForDays: 0,
    gradingRubric: [
      {
        checkpoint: `Checkpoint 1`,
        criteria: [
          { name: `criteria-${Date.now()}`, description: "", points: 0 },
        ],
        total_points: 0,
        project_id: "",
        title: `Checkpoint 1`,
        description: "temp desc",
        checkpoint_mapping: {},
        grading_scale: {},
        is_template: false,
        template_name: "",
        metadata: {},
      },
    ],
  });

  const [projectData, setProjectData] = useState<ProjectData>(
    getDefaultProjectData(),
  );

  const {
    data: courseStaff,
    isLoading: staffLoading,
    isError: staffError,
    error: staffErrorObj,
  } = useStaffMemberQuery(projectData.courseId, true);

  useEffect(() => {
    if (mode === "template" && templateData) {
      // Pre-populate with template data
      const templateProjectData = {
        ...templateData.projectData,
        // Ensure arrays are always initialized
        tags: templateData.projectData.tags || [],
        datasets: templateData.projectData.datasets || [],
        // checkpoints: templateData.projectData.checkpoints || [],
        gradingRubric: templateData.projectData.gradingRubric || [],
        // Keep empty for instance-specific fields unless user wants to pre-fill them
        projectName: "",
        instructor: "",
        teachingAssistant: "",
        // Clear dates for checkpoints
        checkpoints: (templateData.projectData.checkpoints || []).map((cp) => ({
          ...cp,
          start_date: "",
          end_date: "",
        })),
      };

      setProjectData(templateProjectData);

      // Set the first checkpoint as selected
      if (templateProjectData.checkpoints.length > 0) {
        // setSelectedCheckpointId(templateProjectData.checkpoints[0].id);
      }

      // Show success message
      toast.success(`Template "${templateData.name}" loaded successfully!`);

      // Update template usage count
      updateTemplateUsageCount(templateData.id);
    }
  }, [mode, templateData]);

  const updateTemplateUsageCount = (templateId: string) => {
    try {
      const existingTemplates = JSON.parse(
        localStorage.getItem("projectTemplates") || "[]",
      );
      const updatedTemplates = existingTemplates.map(
        (template: TemplateData) =>
          template.id === templateId
            ? { ...template, usageCount: template.usageCount + 1 }
            : template,
      );
      localStorage.setItem(
        "projectTemplates",
        JSON.stringify(updatedTemplates),
      );
    } catch (error) {
      // Failed to update template usage count
    }
  };

  const steps = [
    {
      number: 1,
      title: "Project Details",
      subtitle: "Basic information and requirements",
    },
    {
      number: 2,
      title: "Create Project Assets & Instructions",
      subtitle: "Upload datasets and notebook content",
    },
    {
      number: 3,
      title: "Add Review Checkpoints",
      subtitle: "Set up project milestones and timeline",
    },
    {
      number: 4,
      title: "Project Settings",
      subtitle: "Configure grading and submission settings",
    },
  ];

  const totalMarks = useMemo(() => {
    return (
      projectData?.checkpoints?.reduce((sum, checkpoint) => {
        const checkpointTotal = Array.isArray((checkpoint as any).rubrics)
          ? (checkpoint as any).rubrics.reduce(
              (rubricSum: number, rubric: any) => {
                // If rubric has criteria array, sum from criteria points
                if (
                  rubric.criteria &&
                  Array.isArray(rubric.criteria) &&
                  rubric.criteria.length > 0
                ) {
                  return (
                    rubricSum +
                    rubric.criteria.reduce(
                      (criteriaSum: number, criterion: any) => {
                        return criteriaSum + (criterion.points || 0);
                      },
                      0,
                    )
                  );
                }
                // Fallback to rubric total_points or points if no criteria
                return rubricSum + (rubric.total_points || rubric.points || 0);
              },
              0,
            )
          : 0;
        return sum + checkpointTotal;
      }, 0) || 0
    );
  }, [projectData.checkpoints]);
  useEffect(() => {
    const current = projectData.gradingRubric || [];

    const updated = current.map((item) => {
      const sum = Array.isArray(item.criteria)
        ? item.criteria.reduce((s, c) => s + (Number(c?.points) || 0), 0)
        : Number(item.total_points) || 0;
      return { ...item, total_points: sum };
    });

    const changed = updated.some(
      (it, idx) => (current[idx]?.total_points || 0) !== (it.total_points || 0),
    );

    if (changed) {
      setProjectData((prev) => ({ ...prev, gradingRubric: updated }));
    }
  }, [projectData.gradingRubric]);

  useEffect(() => {}, [projectData?.gradingRubric]);

  const isValidMarks = totalMarks === 100;
  const total_points =
    mode === "edit" ? projectData.totalPoints : projectData.totalPoints;

  const marksError =
    totalMarks > total_points
      ? `Total marks exceed ${total_points} by ${
          totalMarks - total_points
        } marks`
      : totalMarks < total_points
        ? `Total marks are ${
            total_points - totalMarks
          } marks short of ${total_points}`
        : "";

  const canProceed = currentStep < 5 || (currentStep === 5 && isValidMarks);

  const getPageTitle = () => {
    // mode=template with action=edit → "Edit Template"
    if (mode === "template" && action === "edit") {
      return "Edit Template";
    }
    // mode=template → "Create Project from Template"
    if (mode === "template") {
      return "Create Project from Template";
    }
    // mode=edit → "Edit Project"
    if (mode === "edit") {
      return "Edit Project";
    }
    // Default → "Create New Project"
    return "Create New Project";
  };

  const getButtonText = () => {
    // When viewing and mode=edit → "Update Project"
    if (mode === "edit") {
      return "Update Project";
    }
    if (mode === "create") {
      return "Save & Next";
    }
    // When viewing and mode=template with action=edit → "Update Template"
    if (mode === "template" && action === "edit") {
      return "Update Template";
    }
    // When viewing and mode=template → "Create Project"
    if (mode === "template") {
      return "Create Project";
    }
    // Default based on step
    if (currentStep === 4) {
      return "Create Project";
    }
    return "Next";
  };

  const handleConfirmAiScript = () => {
    if (aiScriptPopupState.projectId) {
      generateScriptMutation.mutate(aiScriptPopupState.projectId);
    }
    setAiScriptPopupState({ isOpen: false, projectId: null });
    navigate(paths.manageProjects);
  };

  const handleSkipAiScript = () => {
    setAiScriptPopupState({ isOpen: false, projectId: null });
    navigate(paths.manageProjects);
  };

  function extractProjectId(resp: any) {
    // Handle both cases: full response with data property, or direct data object
    const data = resp?.data || resp;
    if (data?.id) {
      setProjectData((prev) => ({
        ...prev,
        projectId: data.projectId,
        id: data.id,
      }));
    }
  }

  function buildTemplateRubricPayload(
    templateId: string,
    checkpointsToUse: any[],
    templateDetails: any,
  ) {
    const rubricPayload: any[] = [];
    const templateCheckpoints = templateDetails?.checkpoints || [];

    checkpointsToUse.forEach((checkpoint) => {
      const matchingTemplateCheckpoint = templateCheckpoints?.find(
        (tempCp: any) =>
          tempCp.checkpoint_number === checkpoint.checkpoint_number ||
          tempCp.title === checkpoint.title,
      );

      const checkpointId =
        matchingTemplateCheckpoint?.id ||
        checkpoint.id ||
        checkpoint._id ||
        checkpoint.checkpoint_number;

      if (Array.isArray(checkpoint.rubrics) && checkpoint.rubrics.length > 0) {
        checkpoint.rubrics.forEach((rubric: any) => {
          if (rubric.criteria && Array.isArray(rubric.criteria)) {
            rubricPayload.push({
              project_id: templateId,
              checkpoint_id: checkpointId,
              id: "",
              title: rubric.title,
              description: rubric.description,
              criteria: rubric.criteria.map((criterion: any) => ({
                name: criterion.name,
                description: criterion.description,
                points: criterion.points ?? 0,
              })),
              total_points:
                parseInt(rubric.total_points) ||
                rubric.criteria.reduce(
                  (sum: number, c: any) => sum + (c.points ?? 0),
                  0,
                ),
              is_template: true,
            });
          } else if (rubric.name || rubric.description) {
            rubricPayload.push({
              project_id: templateId,
              checkpoint_id: checkpointId,
              id: "",
              title: rubric.name || `${checkpoint.title} Rubric`,
              description: rubric.description || "",
              criteria: [
                {
                  name: rubric.name || "Unnamed Criterion",
                  description: rubric.description || "",
                  points: rubric.points ?? 0,
                },
              ],
              total_points: rubric.points ?? 0,
              is_template: true,
            });
          }
        });
      }
    });

    return rubricPayload;
  }

  const handleSaveAsTemplate = async () => {
    try {
      toast.info("Creating project template...");
      const templatePayload = {
        title: projectData.title,
        description: projectData.description,
        difficulty_level: projectData.difficulty_level,
        estimatedHours: projectData.estimatedHours,
        total_points: projectData.totalPoints,
        learning_objectives: projectData.learning_objectives,
        prerequisites: projectData.prerequisites,
        skills_covered: projectData.tags || [],
        projectType: projectData.projectType,
        totalPoints: projectData.totalPoints,
        dueDate: projectData.dueDate,
        tags: projectData.tags,
        project_overview: projectData.project_overview,
        instructions: projectData.instructions,
        maxSubmissions: projectData.maxSubmissions,
        lateSubmissionsAllowed: projectData.lateSubmissionsAllowed,
        isScreen: 4,
        isTemplate: true,
        templateCategory: projectData.templateCategory || "general",
        templateSubcategory: projectData.templateSubCategory || "",
        resource_profile_id: projectData.resource_profile_id || "",
      };
      const templateResponse =
        await createProjectWithTemplateMutation.mutateAsync(templatePayload);
      if (templateResponse.isSuccess) {
        toast.success("Template created successfully");

        const templateId =
          templateResponse?.data?.projectId || templateResponse?.projectId;
        const templateDescription =
          templateResponse?.data?.description || templateResponse?.description;
        const toFetchTemplateId = templateResponse?.data?.id;

        if (templateId) {
          toast.info("Creating template checkpoints...");
          const templateCheckpointPayload = (
            Array.isArray(projectData.checkpoints)
              ? projectData.checkpoints
              : [projectData.checkpoints]
          ).map((checkpoint, index) => {
            const checkpointNumber = checkpoint.checkpoint_number || index + 1;
            return {
              title: checkpoint.title || `Checkpoint ${checkpointNumber}`,
              checkpoint_number: checkpointNumber,
              isScreen: 3,
              weight_percentage: 10,
              project_id: templateId,
              description: templateDescription,
              start_date: "",
              due_date: "",
              is_required: false,
              status: "draft",
              metadata: {},
            };
          });

          if (templateCheckpointPayload.length > 0) {
            const templateCheckpointResponse =
              await createCheckpointMutation.mutateAsync(
                templateCheckpointPayload,
              );
            if (templateCheckpointResponse?.isSuccess) {
              toast.success("Template checkpoints created successfully");
              toast.info("Fetching template details...");
              const templateDetails = await getTemplateById(toFetchTemplateId);
              if (
                templateDetails?.checkpoints &&
                templateDetails.checkpoints.length > 0
              ) {
                toast.success("Template details fetched successfully");
                toast.info("Creating template rubrics...");
                const templateRubricPayload = buildTemplateRubricPayload(
                  templateId,
                  projectData.checkpoints,
                  templateDetails,
                );

                if (templateRubricPayload.length > 0) {
                  const templateRubricResponse =
                    await createRubricMutation.mutateAsync(
                      templateRubricPayload,
                    );
                  if (templateRubricResponse?.isSuccess) {
                    toast.success("Template rubrics created successfully");
                  } else {
                    toast.warning(
                      "Template checkpoints saved but failed to create rubrics",
                    );
                  }
                }
              }
            }
          }

          if (dataSetsForTemplate && dataSetsForTemplate.length > 0) {
            toast.info("Uploading template datasets...");
            const fd = new FormData();
            dataSetsForTemplate.forEach((file) => {
              fd.append("dataset", file);
            });
            fd.append("projectId", templateId);
            fd.append("isPublic", "false");
            fd.append("isUploadFils", "true");
            const uploadDatasetResponse =
              await uploadDataSetMutation.mutateAsync(fd);
            if (uploadDatasetResponse.isSuccess) {
              toast.success("Template datasets uploaded successfully");
            } else {
              toast.warning(
                uploadDatasetResponse?.message ||
                  "Template saved but failed to upload datasets",
              );
            }
          }
        }
      } else {
        toast.warning("Project saved but failed to create template");
      }
    } catch (error) {
      console.error("Template creation error:", error);
      toast.warning("Project saved but failed to create template");
    }
  };

  const handleConfirmSaveAsTemplate = async () => {
    const projectIdForScript = saveTemplatePopupState.projectId;
    setSaveTemplatePopupState({ isOpen: false, projectId: null });
    setButtonLoader(true);
    try {
      const updatedData = {
        ...projectData,
        saveAsTemplate: true,
      };

      // Set the toggle to true in the local state
      setProjectData(updatedData);

      // Call handleAddProject with explicit data to avoid stale state issues
      const response = await handleAddProject(updatedData);

      if (response?.isSuccess) {
        toast.success("Project saved as template successfully");
      }
    } catch (error) {
      console.error("Failed to save as template:", error);
      toast.error("Failed to save project as template");
    } finally {
      setButtonLoader(false);
      if (
        projectData.auto_grading_enabled &&
        projectData.evaluation_type === "evaluation_script"
      ) {
        setAiScriptPopupState({
          isOpen: true,
          projectId: projectIdForScript,
        });
      } else {
        navigate(paths.manageProjects);
      }
    }
  };

  const handleSkipSaveAsTemplate = () => {
    const projectIdForScript = saveTemplatePopupState.projectId;
    setSaveTemplatePopupState({ isOpen: false, projectId: null });
    if (
      projectData.auto_grading_enabled &&
      projectData.evaluation_type === "evaluation_script"
    ) {
      setAiScriptPopupState({
        isOpen: true,
        projectId: projectIdForScript,
      });
    } else {
      navigate(paths.manageProjects);
    }
  };

  const handleAddProject = async (overrideData?: any) => {
    // Make sure you have access to action
    const dataToUse = overrideData || projectData;

    try {
      setButtonLoader(true);
      dataToUse.isScreen = currentStep;
      console.log("dataToUse", dataToUse);

      let payload: any;

      // Check if mode is template and action is edit
      if (mode === "template" && action === "edit" && projectId) {
        console.log("cond1");
        payload = {
          projectId: projectId,
          title: dataToUse.title,
          description: dataToUse.description,
          difficulty_level: dataToUse.difficulty_level,
          estimatedHours: dataToUse.estimatedHours,
          sandbox_time_duration: dataToUse.sandbox_time_duration,
          dueDate: dataToUse.dueDate,
          total_points: dataToUse.totalPoints,
          learning_objectives: dataToUse.learning_objectives,
          prerequisites: dataToUse.prerequisites,
          skills_covered: dataToUse.tags || [],
          technologies_used: dataToUse.tags || [],
          projectType: dataToUse.projectType,
          totalPoints: dataToUse.totalPoints,
          tags: dataToUse.tags || [],
          project_overview: dataToUse.project_overview,
          instructions: dataToUse.instructions,
          maxSubmissions: dataToUse.maxSubmissions || 1,
          lateSubmissionsAllowed: dataToUse.lateSubmissionsAllowed || false,
          isScreen: 4, // Fixed to 2 as per requirement
          isTemplate: true,
          resource_profile_id: dataToUse.resource_profile_id || "",
          templateCategory: dataToUse.templateCategory,
          templateSubcategory: dataToUse.templateSubCategory,
        };
        try {
          const response =
            await updateTemplateDataMutation.mutateAsync(payload);

          if (response?.isSuccess) {
            queryClient.invalidateQueries({ queryKey: ["project-templates"] });
            queryClient.invalidateQueries({ queryKey: ["templates"] });
            queryClient.invalidateQueries({
              queryKey: ["template", projectId!],
            });
            queryClient.invalidateQueries({
              queryKey: ["project", projectId!],
            });
            toast.success(response.message || "Template updated successfully!");
            //navigate(paths.manageProjects);
          } else {
            toast.error(
              (response as any)?.message || "Failed to update template",
            );
          }

          return response;
        } catch (error: any) {
          toast.error(error?.response?.data?.data?.message);
          return { isSuccess: false, error };
        }
      }
      // Check if mode is template but NOT edit (create template)
      else if (mode === "template" && projectId) {
        console.log("cond2");

        // Keys allowed when creating template
        const templateAllowedKeys = [
          "title",
          "description",
          "template_category",
          "template_subcategory",
          "difficulty_level",
          "estimated_hours",
          "total_points",
          "learning_objectives",
          "prerequisites",
          "skills_covered",
          "technologies_used",
          "tags",
          "dueDate",
          "sandbox_time_duration",
          "resource_profile_id",
        ];

        // Filter function
        const filterKeys = (data: any, allowedKeys: string[]) => {
          return allowedKeys.reduce((acc: any, key) => {
            if (data[key] !== undefined) acc[key] = data[key];
            return acc;
          }, {});
        };

        // Only pick allowed keys for template
        payload = filterKeys(projectData, templateAllowedKeys);

        // Add projectId to the payload
        payload.projectId = projectId;
        payload.id = projectId;
        try {
          const response = await updateMutation.mutateAsync(payload);

          if (response?.isSuccess) {
            queryClient.invalidateQueries({ queryKey: ["project-templates"] });
            queryClient.invalidateQueries({ queryKey: ["templates"] });
            queryClient.invalidateQueries({
              queryKey: ["template", projectId!],
            });
            queryClient.invalidateQueries({
              queryKey: ["project", projectId!],
            });
            toast.success(response.message || "Template updated successfully!");
            navigate(paths.manageProjects);
          } else {
            toast.error(
              (response as any)?.message || "Failed to update template",
            );
          }

          return response;
        } catch (error: any) {
          toast.error(error?.response?.data?.data?.message);
          return { isSuccess: false, error };
        }
      } else {
        console.log("cond3");
        // Normal create/edit flow for non-template modes
        const allowedKeysByStep: Record<number, readonly string[]> = {
          1: [
            "title",
            "categoryId",
            "courseId",
            "description",
            "instructorIds",
            "teachingAssId",
            "projectType",
            "difficulty_level",
            "total_points",
            "estimatedHours",
            "tags",
            "dueDate",
            "project_overview",
            "learning_objectives",
            "prerequisites",
            "totalPoints",
            "isScreen",
            "id",

            "sandbox_time_duration",
            "resource_profile_id",
          ],
          2: [
            "title",
            "categoryId",
            "courseId",
            "description",
            "instructorIds",
            "teachingAssId",
            "projectType",
            "difficulty_level",
            "total_points",
            "estimatedHours",
            "totalPoints",
            "tags",
            "project_overview",
            "learning_objectives",
            "prerequisites",
            "id",
            "dueDate",
            "instructions",
            "isScreen",

            "sandbox_time_duration",
            "resource_profile_id",
          ],
          3: [
            "title",
            "categoryId",
            "courseId",
            "description",
            "instructorIds",
            "teachingAssId",
            "projectType",
            "difficulty_level",
            "total_points",
            "estimatedHours",
            "totalPoints",
            "tags",
            "project_overview",
            "learning_objectives",
            "prerequisites",
            "id",
            "dueDate",
            "instructions",
            "isScreen",

            "sandbox_time_duration",
            "resource_profile_id",
          ],
          4: [
            "title",
            "categoryId",
            "courseId",
            "description",
            "instructorIds",
            "teachingAssId",
            "projectType",
            "difficulty_level",
            "total_points",
            "estimatedHours",
            "tags",
            "project_overview",
            "late_submission_days_allowed",
            "learning_objectives",
            "prerequisites",
            "id",
            "dueDate",
            "instructions",
            "maxSubmissions",
            "totalPoints",
            "lateSubmissionsAllowed",
            "isScreen",
            "isTemplate",
            "sandbox_time_duration",
            "resource_profile_id",
            // Auto-grading
            "auto_grading_enabled",
            "evaluation_type",
            "evaluation_script_s3_key",
          ],
        };

        const filterProjectDataByStep = (data: any, step: number) => {
          const allowedKeys = allowedKeysByStep[step] || [];
          const filteredData = allowedKeys.reduce((acc: any, key) => {
            if (data[key] !== undefined) acc[key] = data[key];
            return acc;
          }, {});

          // Map saveAsTemplate to isTemplate (only for step 4 or if explicitly setting as template)
          if (
            (step === 4 || data.saveAsTemplate === true) &&
            data.saveAsTemplate !== undefined
          ) {
            filteredData.isTemplate = data.saveAsTemplate;
          }
          if (data.templateCategory !== undefined) {
            filteredData.templateCategory = data.templateCategory;
          }
          if (data.templateSubCategory !== undefined) {
            filteredData.templateSubcategory = data.templateSubCategory;
          }

          return filteredData;
        };

        let filteredProjectData = filterProjectDataByStep(
          dataToUse,
          currentStep,
        );

        filteredProjectData.totalPoints = dataToUse.totalPoints;

        // Handle create vs edit
        if (mode === "edit" && projectId) {
          filteredProjectData = {
            ...filteredProjectData,
            id: projectId,
            totalPoints: dataToUse.totalPoints,
          };
        } else if (mode !== "edit" && temporaryProjectId) {
          filteredProjectData = {
            ...filteredProjectData,
            id: temporaryProjectId,
            totalPoints: dataToUse.totalPoints,
          };
        } else {
          delete filteredProjectData.id;
          delete filteredProjectData.projectId;
        }

        const response =
          await createProjectMutation.mutateAsync(filteredProjectData);

        if (response?.isSuccess) {
          extractProjectId(response);

          let successMessage;
          if (mode === "edit") {
            successMessage =
              response.message || "Project updated successfully!";
          } else {
            successMessage =
              response.message || "Project created successfully!";
          }

          toast.success(successMessage);
        } else {
          toast.error(
            (response as any)?.data?.data?.message ||
              "Failed to create project",
          );
        }

        return response;
      }
    } catch (error: any) {
      const fallbackError =
        mode === "edit"
          ? "Failed to update project"
          : "Failed to create project";
      toast.error(
        (error as any)?.response?.data?.data?.message || fallbackError,
      );
      return { isSuccess: false, error };
    } finally {
      setButtonLoader(false);
    }
  };

  const handleNext = async () => {
    if (currentStep <= 4) {
      switch (currentStep) {
        case 1: {
          const response = await handleAddProject();
          setButtonLoader(false);
          if (
            response?.isSuccess &&
            "data" in response &&
            (response as any).data
          ) {
            setTemporaryProjectId((response as any).data.id);
            setCurrentStep((prev) => prev + 1);
          }

          break;
        }

        case 2: {
          setButtonLoader(true);

          try {
            const project_id = mode === "edit" ? projectId : temporaryProjectId;

            if (!project_id) {
              toast.error("Project ID is missing");
              setButtonLoader(false);
              return;
            }

            // Step 1: Handle file deletion for existing PROJECT visibility datasets
            const projectDatasets =
              projectData.datasets?.filter(
                (ds: any) => ds.visibility === "PROJECT",
              ) || [];
            const filesToDelete = projectData.filesToDelete || [];

            if (
              mode === "edit" &&
              filesToDelete.length > 0 &&
              projectDatasets.length > 0
            ) {
              // Find the dataset that contains files to delete and update it via PATCH
              for (const dataset of projectDatasets) {
                // Get the keys that belong to this dataset and are marked for deletion
                const datasetFilesToDelete = filesToDelete.filter(
                  (key: string) =>
                    (dataset as any).s3_keys?.some(
                      (s3File: any) => s3File.key === key,
                    ),
                );

                if (datasetFilesToDelete.length > 0) {
                  try {
                    await updateDatasetMutation.mutateAsync({
                      id: dataset.id,
                      payload: {
                        filesToDelete: datasetFilesToDelete,
                      },
                    });

                    toast.success(
                      `Deleted ${datasetFilesToDelete.length} file(s) from dataset`,
                    );
                  } catch (error) {
                    console.error("[File Deletion Error]", error);
                    toast.error("Failed to delete some files");
                  }
                }
              }

              // Clear filesToDelete from state after deletion
              setProjectData((prev) => ({
                ...prev,
                filesToDelete: [],
              }));
            }

            // Step 2: Handle new file uploads - either add to existing PROJECT dataset or create new
            const uploadedFiles =
              projectData.datasets?.filter((ds) => ds.file) || [];
            let uploadedDatasetIds: string[] = [];

            if (uploadedFiles.length > 0) {
              // Check if there's an existing PROJECT dataset to add files to
              const existingProjectDataset = projectDatasets.find(
                (ds: any) => !ds._markedForDeletion,
              );

              if (mode === "edit" && existingProjectDataset) {
                // Update existing dataset by adding new files via PATCH
                try {
                  const filesToAdd = uploadedFiles.map((ds: any) => ds.file);

                  await updateDatasetMutation.mutateAsync({
                    id: existingProjectDataset.id,
                    payload: {
                      datasetFile: filesToAdd,
                    },
                  });

                  toast.success(
                    `Added ${filesToAdd.length} file(s) to dataset`,
                  );
                  uploadedDatasetIds = [existingProjectDataset.id];
                } catch (error) {
                  console.error("[File Addition Error]", error);
                  toast.error("Failed to add files to dataset");
                  setButtonLoader(false);
                  return;
                }
              } else {
                // Create new dataset(s) from uploaded files
                const uploadResult = await handleUploadedFilesToDatasets(
                  uploadedFiles,
                  project_id,
                  projectData.title || "Project",
                  createDatasetRepoMutation,
                  linkDatasetMutation,
                );

                if (!uploadResult.success) {
                  toast.error(uploadResult.message || "Failed to upload files");
                  setButtonLoader(false);
                  return;
                }

                uploadedDatasetIds = uploadResult.createdDatasetIds;
              }

              // Clear uploaded files from state after successful creation/update
              setProjectData((prev) => ({
                ...prev,
                datasets: prev.datasets?.filter((ds) => !ds.file) || [],
              }));
            }

            // Step 3: Handle dataset linking from repository
            const selectedDatasetIds = projectData.selectedDatasetIds || [];
            const originalDatasetIds =
              projectData.originalLinkedDatasetIds || [];

            // Combine uploaded dataset IDs with selected repository dataset IDs
            const allCurrentDatasetIds = [
              ...selectedDatasetIds,
              ...uploadedDatasetIds,
            ];

            // Handle dataset linking (backend handles unlinking automatically)
            const result = await handleDatasetLinking(
              allCurrentDatasetIds,
              originalDatasetIds,
              project_id,
              linkDatasetMutation,
              (newDatasetIds) => {
                setProjectData((prev) => ({
                  ...prev,
                  originalLinkedDatasetIds: newDatasetIds,
                  selectedDatasetIds: newDatasetIds, // Update selected IDs to include uploaded ones
                  // Also update the legacy single ID for backward compatibility
                  originalLinkedDatasetId: newDatasetIds[0] || "",
                  selectedDatasetId: newDatasetIds[0] || "",
                }));
              },
            );

            if (!result.success) {
              toast.error(result.message || "Dataset operation failed");
              setButtonLoader(false);
              return;
            }

            // Save project state
            const response = await handleAddProject();
            setButtonLoader(false);

            if (response?.isSuccess) {
              setCurrentStep((prev) => prev + 1);
            } else {
              toast.error(
                (response as any)?.data?.data?.message ||
                  "Failed to save project",
              );
            }
          } catch (error) {
            console.error("[Step 2 Error]", error);
            toast.error("An error occurred while processing datasets");
            setButtonLoader(false);
          }

          break;
        }

        case 3: {
          setButtonLoader(true);
          const project_id = mode === "edit" ? projectId : temporaryProjectId;
          // Find the maximum checkpoint_number in dataToMatch.checkpoints
          const maxCheckpointNumber =
            dataToMatch?.checkpoints && dataToMatch.checkpoints.length > 0
              ? Math.max(
                  ...dataToMatch.checkpoints.map(
                    (cp) => cp.checkpoint_number || 0,
                  ),
                )
              : 0;

          let nextCheckpointNumber = maxCheckpointNumber + 1;

          // Track used checkpoint numbers to avoid duplicates within the payload
          const usedCheckpointNumbers = new Set(
            dataToMatch?.checkpoints?.map((cp) => cp.checkpoint_number) || [],
          );

          const checkpointPayload = (
            Array.isArray(projectData.checkpoints)
              ? projectData.checkpoints
              : [projectData.checkpoints]
          ).map((cp, index) => {
            let checkpointNumber = cp.checkpoint_number;

            // If checkpoint_number already exists in dataToMatch OR already used in this payload
            if (usedCheckpointNumbers.has(checkpointNumber)) {
              // Find the next available checkpoint number
              while (usedCheckpointNumbers.has(nextCheckpointNumber)) {
                nextCheckpointNumber++;
              }
              checkpointNumber = nextCheckpointNumber;
              nextCheckpointNumber++;
            }

            // Add this checkpoint number to the used set
            usedCheckpointNumbers.add(checkpointNumber);

            return {
              ...cp,
              checkpoint_number: checkpointNumber,
              isScreen: 3,
              project_id: project_id,
              weight_percentage:
                cp.weight_percentage > 0 && cp.weight_percentage <= 100
                  ? cp.weight_percentage
                  : 10,
            };
          });

          if (checkpointPayload.length === 0) {
            toast.error("No checkpoints to process");
            setButtonLoader(false);
            return;
          }
          try {
            let response;
            // Special case: Template Edit Mode
            if (mode === "template" && action === "edit") {
              // Use projectData.projectId instead of project_id
              const templateProjectId = projectData.projectId;

              if (!templateProjectId) {
                toast.error("Template project ID not found");
                setButtonLoader(false);
                return;
              }

              response = await updateCheckPointsMutation.mutateAsync({
                projectId: templateProjectId,
                checkpoints: checkpointPayload,
              });
            }
            // Existing conditions - only create if no checkpoints exist yet
            else if (
              temporaryProjectId &&
              mode !== "edit" &&
              (!tempCheckPointData ||
                !tempCheckPointData.checkpoints ||
                tempCheckPointData.checkpoints.length === 0)
            ) {
              // No checkpoints exist yet, create new ones
              response =
                await createCheckpointMutation.mutateAsync(checkpointPayload);
            } else if (mode === "edit") {
              response = await updateCheckPointsMutation.mutateAsync({
                project_id,
                checkpoints: checkpointPayload,
              });
            } else {
              const payloadWithoutProjectId = checkpointPayload.map(
                ({ project_id, ...rest }) => rest,
              );

              response = await updateCheckPointsMutation.mutateAsync({
                project_id,
                checkpoints: payloadWithoutProjectId,
              });
            }

            if (response?.isSuccess) {
              setTempCheckPointData(response);
              projectData.isScreen = checkpointPayload[0].isScreen;

              const projectResponse = await handleAddProject();
              if (!projectResponse?.isSuccess) {
                toast.error((projectResponse as any)?.data?.data?.message);
              }

              setCurrentStep(currentStep + 1);
              setButtonLoader(false);
            } else {
              toast.error(response?.message || "Failed to process checkpoints");
              setButtonLoader(false);
            }
          } catch (error) {
            setButtonLoader(false);
            toast.error(
              (error as any)?.response?.data?.data?.message ||
                "Failed to process checkpoints",
            );
          }

          break;
        }
        case 4: {
          setButtonLoader(true);

          let project_id: string;

          if (mode === "template" && action === "edit") {
            project_id = projectData.projectId || "";
          } else {
            project_id = mode === "edit" ? projectId || "" : temporaryProjectId;
          }
          const checkpointsToUse =
            mode === "edit"
              ? (projectData?.checkpoints ?? [])
              : projectData.checkpoints;

          if (checkpointsToUse.length === 0) {
            toast.error("No checkpoints available to create rubric");
            setButtonLoader(false);
            return;
          }
          let rubricPayload: any[] = [];

          if (mode === "edit") {
            function transformCheckpointsToRubricPayload(
              checkpointsData,
              projectId,
            ) {
              const rubrics: any[] = [];

              checkpointsData.forEach((checkpoint) => {
                if (checkpoint.rubrics && Array.isArray(checkpoint.rubrics)) {
                  checkpoint.rubrics.forEach((rubric) => {
                    const criteria = rubric.criteria?.map((c) => ({
                      name: c.name,
                      description: c.description,
                      points: c.points,
                    })) || [
                      {
                        name: rubric.name,
                        description: rubric.description,
                        points: rubric.points || 0,
                      },
                    ];

                    const total_points =
                      rubric.total_points ||
                      criteria.reduce((sum, c) => sum + (c.points || 0), 0);

                    rubrics.push({
                      id: rubric.id || "",
                      checkpoint_id: checkpoint.id,
                      title: rubric.title || rubric.name,
                      description: rubric.description,
                      criteria,
                      total_points,
                      is_template: rubric.is_template || false,
                    });
                  });
                }
              });

              return rubrics;
            }

            rubricPayload = transformCheckpointsToRubricPayload(
              checkpointsToUse,
              project_id,
            );
          } else if (mode === "template" && action === "edit") {
            function transformTemplateCheckpointsToRubricPayload(
              checkpointsData,
              projectId,
            ) {
              const rubrics: any[] = [];

              checkpointsData.forEach((checkpoint) => {
                if (checkpoint.rubrics && Array.isArray(checkpoint.rubrics)) {
                  checkpoint.rubrics.forEach((rubric) => {
                    const criteria = rubric.criteria?.map((c) => ({
                      name: c.name,
                      description: c.description,
                      points: c.points,
                    })) || [
                      {
                        name: rubric.name,
                        description: rubric.description,
                        points: rubric.points || 0,
                      },
                    ];

                    const total_points =
                      rubric.total_points ||
                      criteria.reduce((sum, c) => sum + (c.points || 0), 0);

                    rubrics.push({
                      id: rubric.id || "",
                      checkpoint_id: checkpoint.id,
                      title: rubric.title || rubric.name,
                      description: rubric.description,
                      criteria,
                      total_points,
                      is_template: false,
                    });
                  });
                }
              });

              return rubrics;
            }

            rubricPayload = transformTemplateCheckpointsToRubricPayload(
              checkpointsToUse,
              project_id,
            );
          } else {
            function buildRubricPayload(
              project_id: string,
              checkpointsToUse: any[],
              tempCheckpointData: any,
            ) {
              const rubricPayload: any = [];
              const tempCheckpoints = tempCheckpointData || [];
              checkpointsToUse.forEach((checkpoint) => {
                const matchingTempCheckpoint = tempCheckpoints?.find(
                  (tempCp) =>
                    tempCp.checkpoint_number === checkpoint.checkpoint_number ||
                    tempCp.title === checkpoint.title,
                );

                const checkpointId =
                  matchingTempCheckpoint?.id ||
                  checkpoint.id ||
                  checkpoint._id ||
                  checkpoint.checkpoint_number;

                if (
                  Array.isArray(checkpoint.rubrics) &&
                  checkpoint.rubrics.length > 0
                ) {
                  checkpoint.rubrics.forEach((rubric: any) => {
                    if (rubric.criteria && Array.isArray(rubric.criteria)) {
                      rubricPayload.push({
                        project_id,
                        checkpoint_id: checkpointId,
                        id: rubric.id || "",
                        title: rubric.title,
                        description: rubric.description,
                        criteria: rubric.criteria.map((criterion: any) => ({
                          name: criterion.name,
                          description: criterion.description,
                          points: criterion.points ?? 0,
                        })),
                        total_points:
                          parseInt(rubric.total_points) ||
                          rubric.criteria.reduce(
                            (sum, c) => sum + (c.points ?? 0),
                            0,
                          ),
                        is_template: rubric.is_template ?? false,
                      });
                    } else if (rubric.name || rubric.description) {
                      rubricPayload.push({
                        project_id,
                        checkpoint_id: checkpointId,
                        id: "",
                        title: rubric.name || `${checkpoint.title} Rubric`,
                        description: rubric.description || "",
                        criteria: [
                          {
                            name: rubric.name || "Unnamed Criterion",
                            description: rubric.description || "",
                            points: rubric.points ?? 0,
                          },
                        ],
                        total_points: rubric.points ?? 0,
                        is_template: false,
                      });
                    }
                  });
                }
              });

              return rubricPayload;
            }

            rubricPayload = buildRubricPayload(
              project_id,
              checkpointsToUse,
              tempCheckPointData?.checkpoints || [],
            );
          }

          if (rubricPayload.length === 0) {
            toast.error("No rubric items to create");
            setButtonLoader(false);
            return;
          }

          try {
            let hasExistingRubrics: boolean;
            let rubricMutation;

            if (mode === "edit") {
              hasExistingRubrics =
                Array.isArray(baseProjectData?.gradingRubric) &&
                baseProjectData.gradingRubric.length > 0;
              rubricMutation = hasExistingRubrics
                ? updateRubricMutation
                : createRubricMutation;
            } else if (mode === "template" && action === "edit") {
              hasExistingRubrics =
                Array.isArray(projectData?.gradingRubric) &&
                projectData.gradingRubric.length > 0;
              rubricMutation = updateRubricMutation;
            } else {
              hasExistingRubrics = false;
              rubricMutation = createRubricMutation;
            }

            toast.info(
              mode === "template" && action === "edit"
                ? "Updating template rubrics..."
                : hasExistingRubrics
                  ? "Updating project rubrics..."
                  : "Creating project rubrics...",
            );

            const rubricResponse = await rubricMutation.mutateAsync(
              rubricMutation === updateRubricMutation
                ? { project_id, rubrics: rubricPayload }
                : rubricPayload,
            );

            if (rubricResponse.isSuccess) {
              toast.success(
                mode === "template" && action === "edit"
                  ? "Template rubrics updated successfully"
                  : hasExistingRubrics
                    ? "Project rubrics updated successfully"
                    : "Project rubrics created successfully",
              );

              projectData.isScreen =
                projectData.isScreen && projectData.isScreen > currentStep
                  ? projectData.isScreen
                  : currentStep;

              const response = await handleAddProject(projectData);

              if (response?.isSuccess) {
                // The project is already marked as a template in the backend via handleAddProject
                // if projectData.saveAsTemplate was true. So we skip handleSaveAsTemplate()
                // to avoid creating a duplicate project.

                if (canProceed) {
                  setButtonLoader(false);

                  // If NOT saving as template automatically, and NOT in edit mode, ASK user
                  if (
                    !projectData.saveAsTemplate &&
                    mode !== "edit" &&
                    !(mode === "template" && action === "edit")
                  ) {
                    setSaveTemplatePopupState({
                      isOpen: true,
                      projectId: project_id, // Use current project ID
                    });
                  } else if (
                    projectData.auto_grading_enabled &&
                    projectData.evaluation_type === "evaluation_script"
                  ) {
                    setAiScriptPopupState({
                      isOpen: true,
                      projectId: project_id,
                    });
                  } else {
                    navigate(paths.manageProjects);
                  }
                }
              } else {
                toast.error(
                  mode === "template" && action === "edit"
                    ? "Rubric updated but failed to save template"
                    : hasExistingRubrics
                      ? "Rubric updated but failed to save project"
                      : "Rubric created but failed to save project",
                );
                setButtonLoader(false);
              }
            } else {
              toast.error(
                rubricResponse?.data?.data?.message ||
                  (mode === "template" && action === "edit"
                    ? "Failed to update template rubric"
                    : hasExistingRubrics
                      ? "Failed to update rubric"
                      : "Failed to create rubric"),
              );
              setButtonLoader(false);
            }
          } catch (error: any) {
            const hasExistingRubrics = baseProjectData?.checkpoints?.some(
              (cp: any) => Array.isArray(cp.rubrics) && cp.rubrics.length > 0,
            );
            setButtonLoader(false);
            toast.error(
              error?.response?.data?.data?.message ||
                (mode === "template" && action === "edit"
                  ? "Failed to update template rubric"
                  : hasExistingRubrics
                    ? "Failed to update rubric"
                    : "Failed to create rubric"),
            );
          }

          break;
        }
      }
    }
  };

  const handleNextPage = () => {
    if (currentStep < 4) {
      setLocalNavigate(true);
      setCurrentStep(currentStep + 1);
    }
  };

  const handleBack = () => {
    if (currentStep > 1) {
      setLocalNavigate(true);
      setCurrentStep(currentStep - 1);
    }
  };

  const handleCancel = () => {
    navigate(paths.manageProjects);
    //    navigateTo("manage-projects");
  };

  const addCheckpoint = () => {
    // compute new checkpoint number from current state
    const newCheckpointNumber = projectData.checkpoints.length
      ? Math.max(
          ...projectData.checkpoints.map((c) => Number(c.checkpoint_number)),
        ) + 1
      : 1;

    const newCheckpoint: checkpoints = {
      isScreen: projectData.isScreen ?? 0,
      title: `Checkpoint`,
      description: "",
      checkpoint_number: newCheckpointNumber,

      start_date: "",
      due_date: "",
      weight_percentage: 0,
      is_required: false,
      status: "draft",
      metadata: {},
    };

    const newRubricItem = {
      checkpoint: `Checkpoint ${newCheckpointNumber}`,
      checkpoint_mapping: {},
      criteria: [
        { name: `criteria-${Date.now()}`, description: "", points: 0 },
      ],
      total_points: 0,
      project_id: projectData.id || "",
      title: `Checkpoint ${newCheckpointNumber}`,
      description: "temp desc",
      grading_scale: {},
      is_template: false,
      template_name: "",
      metadata: {},
    };

    setProjectData((prev) => ({
      ...prev,
      checkpoints: [...(prev.checkpoints || []), newCheckpoint],
      gradingRubric: [...(prev.gradingRubric || []), newRubricItem],
    }));

    // select the newly created checkpoint (selectedCheckpointId is a number)
    setSelectedCheckpointId(newCheckpointNumber);
  };

  const updateCheckpoint = (
    id: number,
    field: string,
    value: string | boolean,
  ) => {
    // Always update the state
    setProjectData((prev) => ({
      ...prev,
      checkpoints: prev.checkpoints.map((cp) =>
        cp.checkpoint_number === id ? { ...cp, [field]: value } : cp,
      ),
    }));

    // Force remount with new key when clearing dates
  };

  const updateRubricItem = (
    index: number,
    field: string,
    value: string | number,
  ) => {
    setProjectData((prev) => ({
      ...prev,
      gradingRubric: prev.gradingRubric.map((item, i) =>
        i === index ? { ...item, [field]: value } : item,
      ),
    }));
  };

  const Breadcrumb_Component = () => (
    <div className="flex items-start p-4">
      <Breadcrumb className="bg-transparent border-none shadow-none">
        <BreadcrumbList className="bg-transparent">
          <BreadcrumbItem>
            <BreadcrumbLink
              onClick={() => navigate(paths.root)}
              className="cursor-pointer flex items-center"
            >
              <Home className="h-4 w-4" />
            </BreadcrumbLink>
          </BreadcrumbItem>
          <BreadcrumbSeparator />
          <BreadcrumbItem>
            <BreadcrumbLink
              onClick={() => navigate(paths.manageProjects)}
              className="cursor-pointer flex items-center underline"
            >
              <FolderOpen className="h-4 w-4 mr-1" />
              Project Management
            </BreadcrumbLink>
          </BreadcrumbItem>
          <BreadcrumbSeparator />
          <BreadcrumbItem>
            <BreadcrumbPage className="bg-transparent">
              {getPageTitle()}
            </BreadcrumbPage>
          </BreadcrumbItem>
        </BreadcrumbList>
      </Breadcrumb>
    </div>
  );

  const TemplateInfoBanner = () => {
    if (mode !== "template" || !templateData) return null;

    return (
      <div className="mb-6 p-4 bg-blue-50 border border-blue-200 rounded-lg">
        <div className="flex items-start space-x-3">
          <Copy className="h-5 w-5 text-bits-blue mt-0.5" />
          <div className="flex-1">
            <h3 className="text-sm font-medium text-blue-900">
              Creating project from template: "{templateData.name}"
            </h3>
            <p className="text-sm text-blue-700 mt-1">
              {templateData.description}
            </p>
            <div className="flex items-center space-x-4 mt-2 text-xs text-bits-blue">
              <span>Category: {templateData.category}</span>
              <span>•</span>
              <span>Used {templateData.usageCount} times</span>
              <span>•</span>
              <span>Created by {templateData.createdBy}</span>
            </div>
          </div>
        </div>
      </div>
    );
  };

  useEffect(() => {}, [
    courses,
    projectData.courseId,
    projectData.instructorIds,
    projectData.teachingAssId,
  ]);

  const queryClient = useQueryClient();
  const navigate = useNavigate();

  const navigateBack = () => {
    navigate(paths.manageProjects);
  };

  return (
    <div className="bg-gray-50 min-h-screen">
      <div className="p-6">
        <div className="mb-6">
          <Breadcrumb_Component />
        </div>

        <TemplateInfoBanner />

        <div className="flex items-center space-x-4 mb-12">
          <Button variant="ghost" size="sm" onClick={navigateBack}>
            <ArrowLeft className="h-4 w-4" />
          </Button>
          <h1 className="text-2xl font-semibold text-gray-900">
            {getPageTitle()}
          </h1>
          {mode === "template" && templateData && (
            <div className="ml-auto text-sm text-gray-500">
              Template: {templateData.name}
            </div>
          )}
        </div>

        {/* Step Indicator - Increased spacing to content by 1.5x */}
        <StepIndicator steps={steps} currentStep={currentStep} />

        {/* Step Content - Full Width */}
        <div className="mb-8">
          {currentStep === 1 && (
            <ProjectDetailsStep
              projectData={projectData}
              setProjectData={setProjectData}
              courses={courses}
              coursesLoading={coursesLoading}
              coursesError={coursesError}
              coursesErrorObj={coursesErrorObj}
              courseStaff={courseStaff}
              staffLoading={staffLoading}
              staffError={staffError}
              staffErrorObj={staffErrorObj}
              temporaryProjectId={temporaryProjectId}
              templateCategory={templateCategory}
              templateSubcategory={templateSubcategory}
              savingTemplate={savingTemplate}
              setTemplateCategory={setTemplateCategory}
              setTemplateSubcategory={setTemplateSubcategory}
              setSavingTemplate={setSavingTemplate}
              setCurrentStep={setCurrentStep}
              isLoading={false}
            />
          )}
          {currentStep === 2 && (
            <ProjectAssetsStep
              projectData={projectData}
              temporaryProjectId={temporaryProjectId}
              setProjectData={setProjectData}
              setCurrentStep={setCurrentStep}
            />
          )}
          {currentStep === 3 && (
            <ReviewCheckpointsStep
              projectData={projectData}
              dataToMatch={dataToMatch}
              setDataToMatch={setDataToMatch}
              setProjectData={setProjectData}
              selectedCheckpointId={selectedCheckpointId}
              setSelectedCheckpointId={setSelectedCheckpointId}
              addCheckpoint={addCheckpoint}
              updateCheckpoint={updateCheckpoint}
              temporaryProjectId={temporaryProjectId}
              setCurrentStep={setCurrentStep}
            />
          )}

          {currentStep === 4 && (
            <ProjectSettingsStep
              projectData={projectData}
              setProjectData={setProjectData}
              updateRubricItem={updateRubricItem}
              totalMarks={totalMarks}
              isValidMarks={isValidMarks}
              marksError={marksError}
              setBaseProjectData={setBaseProjectData}
              temporaryProjectId={temporaryProjectId}
              setTempCheckPointData={setTempCheckPointData}
              setCurrentStep={setCurrentStep}
            />
          )}
        </div>

        {/* Navigation Buttons */}
        <div className="flex justify-between">
          <div className="flex justify-between gap-2">
            <div>
              {currentStep > 1 && (
                <Button variant="outline" onClick={handleBack}>
                  <ArrowLeft className="h-4 w-4 mr-2" />
                  Back
                </Button>
              )}
            </div>
            <div>
              {currentStep < 4 && (
                <Button
                  className="bg-bits-blue hover:bg-bits-blue"
                  onClick={handleNextPage}
                >
                  Next
                  <ArrowRight className="h-4 w-4 ml-2" />
                </Button>
              )}
            </div>
          </div>
          <div className="flex space-x-3">
            <Button
              className="border border-bits-blue text-bits-blue bg-white hover:bg-gray-50 hover:text-primary"
              onClick={handleCancel}
            >
              Cancel
            </Button>

            <Button
              onClick={handleNext}
              className="bg-bits-blue hover:bg-bits-blue"
              disabled={!canProceed || buttonLoader}
            >
              {buttonLoader ? (
                <div className="flex items-center">
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                  Processing...
                </div>
              ) : (
                <>
                  {getButtonText()}
                  {currentStep < 4 && <ArrowRight className="h-4 w-4 mr-2" />}
                </>
              )}
            </Button>
          </div>
        </div>
      </div>

      <ConfirmDialog
        open={aiScriptPopupState.isOpen}
        title="Generate AI Evaluation Script"
        description="Would you like the AI to automatically gather your project instructions, checkpoints, and rubrics to generate a custom Python evaluation script in the background?"
        confirmText="Confirm"
        cancelText="Skip for Now"
        onConfirm={handleConfirmAiScript}
        onCancel={handleSkipAiScript}
        confirmVariant="default"
        confirmClassName="bg-bits-blue hover:bg-bits-blue text-white"
      />

      <ConfirmDialog
        open={saveTemplatePopupState.isOpen}
        title="Save as Template"
        description="Would you like to save this project as a template? This will allow you and other instructors to reuse the structure, checkpoints, and rubrics for future projects."
        confirmText="Save as Template"
        cancelText="Skip"
        onConfirm={handleConfirmSaveAsTemplate}
        onCancel={handleSkipSaveAsTemplate}
        confirmVariant="default"
        confirmClassName="bg-bits-blue hover:bg-bits-blue text-white"
      />
    </div>
  );
}

interface Props {
  onChange?: (formattedTime: string) => void;
  value?: string; // Optional prop to set initial value
}

function TimeDurationInputs({ onChange, value }: Props) {
  // Initialize from value prop if provided (format: "HHH:MM")
  const parseInitialValue = useCallback((val?: string) => {
    if (!val || typeof val !== "string") return { hours: "", minutes: "" };
    if (!val.includes(":")) return { hours: "", minutes: "" };
    const [h, m] = val.split(":");
    // Keep as strings, but remove leading zeros for hours (except when it's "0")
    // For minutes, preserve the format (e.g., "00" stays "00")
    const hoursStr = h ? String(parseInt(h) || 0) : "";
    const minutesStr = m ? String(parseInt(m) || 0) : ""; // Parse minutes to remove leading zeros
    return {
      hours: hoursStr,
      minutes: minutesStr,
    };
  }, []);

  const initialValue = parseInitialValue(value);
  const [hours, setHours] = useState(initialValue.hours);
  const [minutes, setMinutes] = useState(initialValue.minutes);

  // Update hours and minutes when value prop changes
  useEffect(() => {
    if (value && typeof value === "string" && value.includes(":")) {
      const parsed = parseInitialValue(value);
      setHours(parsed.hours);
      setMinutes(parsed.minutes);
    }
  }, [value, parseInitialValue]);

  const formatTime = (h: string, m: string) => {
    const hoursNum = parseInt(h) || 0;
    const minutesNum = parseInt(m) || 0;
    const formattedHours = hoursNum.toString().padStart(3, "0");
    const formattedMinutes = minutesNum.toString().padStart(2, "0");
    return `${formattedHours}:${formattedMinutes}`;
  };

  const handleHoursChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const inputValue = e.target.value;

    // Allow only digits and limit to 3 characters
    if (/^\d{0,3}$/.test(inputValue)) {
      const numValue = parseInt(inputValue) || 0;
      // Limit to maximum 999 hours
      if (inputValue === "" || numValue <= 999) {
        setHours(inputValue);
        // Don't call onChange during typing, only on blur
      }
    }
  };

  const handleMinutesChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const inputValue = e.target.value;

    // Allow only digits and limit to 2 characters
    if (/^\d{0,2}$/.test(inputValue)) {
      // If empty, allow it
      if (inputValue === "") {
        setMinutes(inputValue);
        return;
      }

      // Check if the value is valid (0-59)
      const numValue = parseInt(inputValue, 10);
      if (!isNaN(numValue) && numValue >= 0 && numValue <= 59) {
        setMinutes(inputValue);
      }
      // Don't call onChange during typing, only on blur
    }
  };

  const handleHoursBlur = () => {
    // Ensure minimum 1 digit display on blur
    if (hours === "") {
      setHours("0");
      onChange?.(formatTime("0", minutes));
    } else {
      // Update with current values on blur
      onChange?.(formatTime(hours, minutes));
    }
  };

  const handleMinutesBlur = () => {
    // Ensure minimum 1 digit display on blur
    if (minutes === "") {
      setMinutes("0");
      onChange?.(formatTime(hours, "0"));
    } else {
      // Validate and update on blur
      const numValue = parseInt(minutes, 10);
      if (!isNaN(numValue) && numValue >= 0 && numValue <= 59) {
        onChange?.(formatTime(hours, minutes));
      } else {
        // Reset to 0 if invalid
        setMinutes("0");
        onChange?.(formatTime(hours, "0"));
      }
    }
  };

  const handleHoursKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    // Allow: backspace, delete, tab, escape, enter, arrows, home, end
    if (
      [8, 9, 27, 13, 46, 35, 36, 37, 38, 39, 40].includes(e.keyCode) ||
      // Allow Ctrl+A, Ctrl+C, Ctrl+V, Ctrl+X
      (e.keyCode === 65 && e.ctrlKey) ||
      (e.keyCode === 67 && e.ctrlKey) ||
      (e.keyCode === 86 && e.ctrlKey) ||
      (e.keyCode === 88 && e.ctrlKey)
    ) {
      return;
    }
    // Ensure that it's a number and stop the keypress
    if (
      (e.shiftKey || e.keyCode < 48 || e.keyCode > 57) &&
      (e.keyCode < 96 || e.keyCode > 105)
    ) {
      e.preventDefault();
    }
  };

  const handleMinutesKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    // Same validation as hours
    if (
      [8, 9, 27, 13, 46, 35, 36, 37, 38, 39, 40].includes(e.keyCode) ||
      (e.keyCode === 65 && e.ctrlKey) ||
      (e.keyCode === 67 && e.ctrlKey) ||
      (e.keyCode === 86 && e.ctrlKey) ||
      (e.keyCode === 88 && e.ctrlKey)
    ) {
      return;
    }
    if (
      (e.shiftKey || e.keyCode < 48 || e.keyCode > 57) &&
      (e.keyCode < 96 || e.keyCode > 105)
    ) {
      e.preventDefault();
    }
  };

  return (
    <div className="flex items-center gap-2">
      {/* Hours Input */}
      <div className="flex flex-col">
        <input
          id="hours"
          type="text"
          value={hours}
          onChange={handleHoursChange}
          onBlur={handleHoursBlur}
          onKeyDown={handleHoursKeyDown}
          placeholder="000"
          maxLength={3}
          className="w-16 px-2 py-2 text-sm text-center text-gray-600 border border-gray-300 rounded bg-bits-grey-100 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
        />
        <label className="text-xs text-gray-500 text-center mt-1">Hours</label>
      </div>

      {/* Separator */}
      <div className="text-lg font-semibold text-gray-400 mb-4">:</div>

      {/* Minutes Input */}
      <div className="flex flex-col">
        <input
          id="minutes"
          type="text"
          value={minutes}
          onChange={handleMinutesChange}
          onBlur={handleMinutesBlur}
          onKeyDown={handleMinutesKeyDown}
          placeholder="00"
          maxLength={2}
          className="w-14 px-2 py-2 text-sm text-center text-gray-600 border border-gray-300 rounded bg-bits-grey-100 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
        />
        <label className="text-xs text-gray-500 text-center mt-1">
          Minutes
        </label>
      </div>

      {/* Display formatted time */}
      <div className="ml-2 text-sm text-gray-600 font-mono">
        {formatTime(hours, minutes)}
      </div>
    </div>
  );
}

type Item = {
  id: string;
  name: string;
  // allow arbitrary extra fields
  [k: string]: any;
};

type CourseStaffProps = {
  items: Item[]; // list of selectable items
  selectedIds: string[]; // controlled selected ids
  onChange: (ids: string[]) => void; // controlled change handler
  label?: string; // optional label rendered inside the trigger
  placeholder?: string; // placeholder when nothing selected
  width?: string; // tailwind width class, e.g. "w-72"
  search?: boolean; // enable a simple search box in dropdown
  showSelectAll?: boolean; // show 'Select all' control
  className?: string; // extra classes for trigger
  projectData: any;
  setProjectData: React.Dispatch<React.SetStateAction<ProjectData>>;
  teachinAssistants: any[];
  valueToCheck: string;
};

export function GenericMultiSelect({
  items,
  label,
  placeholder = "-- Select --",
  width = "w-72",
  search = true,
  showSelectAll = true,
  className = "",
  projectData,
  setProjectData,
  valueToCheck,
}: CourseStaffProps) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const rootRef = useRef<HTMLDivElement | null>(null);
  const [searchParams] = useSearchParams();
  const mode = searchParams.get("mode");
  const action = searchParams.get("action");
  useEffect(() => {
    if (mode != "edit") return;
  }, [projectData]);

  useEffect(() => {
    if (
      mode === "edit" &&
      projectData[valueToCheck] &&
      !projectData[`${valueToCheck}Ids`]?.length
    ) {
      setProjectData((prev: any) => ({
        ...prev,
        [`${valueToCheck}Ids`]: projectData[valueToCheck].map((i: any) =>
          String(i.id),
        ),
      }));
    }
  }, [mode, projectData[valueToCheck]]);

  // Close dropdown on outside click
  useEffect(() => {
    function handleClickOutside(e: MouseEvent) {
      if (!rootRef.current) return;
      if (!rootRef.current.contains(e.target as Node)) setOpen(false);
    }
    document.addEventListener("click", handleClickOutside);
    return () => document.removeEventListener("click", handleClickOutside);
  }, []);

  // Filter items based on search query
  const filtered = query
    ? items.filter(
        (it) =>
          String(it.name)?.toLowerCase()?.includes(query.toLowerCase()) ||
          String(it.id)?.toLowerCase()?.includes(query.toLowerCase()),
      )
    : items;

  // Compute dynamic selected IDs
  // Handle the field name mapping: "instructors" -> "instructorIds", "teachingAssistants" -> "teachingAssId"
  const idsFieldName =
    valueToCheck === "instructors"
      ? "instructorIds"
      : valueToCheck === "teachingAssistants"
        ? "teachingAssId"
        : `${valueToCheck}Ids`;

  const dynamicSelectedIds: string[] =
    projectData[idsFieldName] ??
    (mode === "edit" && projectData[valueToCheck]
      ? projectData[valueToCheck].map((i: any) => String(i.id))
      : []);

  // Toggle selection
  const toggleId = (id: string) => {
    const exists = dynamicSelectedIds.includes(id);
    const updatedIds = exists
      ? dynamicSelectedIds.filter((x) => x !== id)
      : [...dynamicSelectedIds, id];

    setProjectData((prev: any) => {
      // Determine the correct field names based on valueToCheck
      let primaryIdsField = ""; // instructorIds or teachingAssId
      let backendFieldName = "";
      let altFieldName = "";

      if (valueToCheck === "instructors") {
        primaryIdsField = "instructorIds";
        backendFieldName = "instructor_id";
        altFieldName = "instructorsIds";
      } else if (valueToCheck === "teachingAssistants") {
        primaryIdsField = "teachingAssId";
        backendFieldName = "teaching_ass_id";
        altFieldName = "teachingAssistantsIds";
      }

      const newData = {
        ...prev,
        [primaryIdsField]: updatedIds,
        ...(altFieldName ? { [altFieldName]: updatedIds } : {}),
        ...(backendFieldName ? { [backendFieldName]: updatedIds } : {}),
      };
      return newData;
    });
  };

  // Select all / clear all
  const selectAll = () => {
    const allIds = items.map((it) => String(it.id));

    setProjectData((prev: any) => {
      // Determine the correct field names based on valueToCheck
      let primaryIdsField = ""; // instructorIds or teachingAssId
      let backendFieldName = "";
      let altFieldName = "";

      if (valueToCheck === "instructors") {
        primaryIdsField = "instructorIds";
        backendFieldName = "instructor_id";
        altFieldName = "instructorsIds";
      } else if (valueToCheck === "teachingAssistants") {
        primaryIdsField = "teachingAssId";
        backendFieldName = "teaching_ass_id";
        altFieldName = "teachingAssistantsIds";
      }

      return {
        ...prev,
        [primaryIdsField]: allIds,
        ...(altFieldName ? { [altFieldName]: allIds } : {}),
        ...(backendFieldName ? { [backendFieldName]: allIds } : {}),
      };
    });
  };

  const clearAll = () => {
    setProjectData((prev: any) => {
      // Determine the correct field names based on valueToCheck
      let primaryIdsField = ""; // instructorIds or teachingAssId
      let backendFieldName = "";
      let altFieldName = "";

      if (valueToCheck === "instructors") {
        primaryIdsField = "instructorIds";
        backendFieldName = "instructor_id";
        altFieldName = "instructorsIds";
      } else if (valueToCheck === "teachingAssistants") {
        primaryIdsField = "teachingAssId";
        backendFieldName = "teaching_ass_id";
        altFieldName = "teachingAssistantsIds";
      }

      const newData = {
        ...prev,
        [valueToCheck]: [], // Clear the objects array (instructors/teachingAssistants)
        [primaryIdsField]: [], // Clear the primary IDs field (instructorIds/teachingAssId)
        ...(altFieldName ? { [altFieldName]: [] } : {}), // Clear alternate field (instructorsIds/teachingAssistantsIds)
        ...(backendFieldName ? { [backendFieldName]: [] } : {}), // Clear backend field
      };
      return newData;
    });
  };

  // Display selected names in button
  const labelStr =
    dynamicSelectedIds.length > 0
      ? dynamicSelectedIds
          .map((id) => items.find((it) => String(it.id) === id)?.name ?? id)
          .join(", ")
      : (label ?? placeholder);

  return (
    <div className="relative inline-block text-left" ref={rootRef}>
      {/* Button showing selected names */}
      <Button
        type="button"
        variant="outline"
        onClick={() => setOpen((v) => !v)}
        className={`${width} text-left text-sm font-normal text-bits-grey-500 bg-bits-grey-100 px-3 py-2 rounded flex items-center justify-between ${className}`}
        aria-haspopup="menu"
        aria-expanded={open}
      >
        <span className="truncate">{labelStr}</span>
        <span className="ml-2">▾</span>
      </Button>

      {/* Dropdown menu */}
      {open && (
        <div
          className="absolute mt-2 bg-white shadow-lg border rounded z-50 max-h-72 overflow-auto p-2"
          style={{ minWidth: 280 }}
          role="menu"
        >
          <div className="flex flex-col gap-2">
            {search && (
              <input
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder="Search..."
                className="px-2 py-1 border rounded text-sm"
              />
            )}

            <div className="flex items-center justify-between gap-2">
              {showSelectAll && (
                <div className="flex gap-2">
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    onClick={selectAll}
                    className="text-sm"
                  >
                    Select all
                  </Button>
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    onClick={clearAll}
                    className="text-sm"
                  >
                    Clear
                  </Button>
                </div>
              )}

              <Button
                type="button"
                variant="secondary"
                size="sm"
                onClick={() => setOpen(false)}
                className="text-sm"
              >
                Done
              </Button>
            </div>

            <div className="divide-y overflow-auto">
              {filtered.length === 0 ? (
                <div className="p-2 text-sm text-gray-500">No items</div>
              ) : (
                filtered.map((it) => {
                  const idStr = String(it.id);
                  const checked = dynamicSelectedIds.includes(idStr);
                  return (
                    <Button
                      key={idStr}
                      type="button"
                      variant="ghost"
                      onClick={() => toggleId(idStr)}
                      className={`w-full flex items-center justify-between gap-2 p-2 rounded hover:bg-gray-50 text-left h-auto ${
                        checked ? "bg-bits-grey-50" : ""
                      }`}
                    >
                      <div className="text-sm truncate">
                        {it.name}{" "}
                        {it.email && (
                          <span className="text-xs text-gray-400">
                            ({it.email})
                          </span>
                        )}
                      </div>
                      {checked ? (
                        <svg
                          xmlns="http://www.w3.org/2000/svg"
                          className="h-4 w-4"
                          viewBox="0 0 20 20"
                          fill="currentColor"
                        >
                          <path
                            fillRule="evenodd"
                            d="M16.707 5.293a1 1 0 010 1.414l-7.414 7.414a1 1 0 01-1.414 0L3.293 9.414a1 1 0 011.414-1.414L8 11.293l6.293-6.293a1 1 0 011.414 0z"
                            clipRule="evenodd"
                          />
                        </svg>
                      ) : (
                        <span className="w-4 h-4" />
                      )}
                    </Button>
                  );
                })
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
